// shared.js — Общие утилиты, тема, UI-компоненты
// Все файлы-компоненты импортируют из этого файла
// Тексты берутся из strings.js для поддержки мультиязычности
import React,{useState,useEffect,useRef,createContext,useContext} from 'react';
import ReactDOM from 'react-dom';
import{S,getLang}from'./strings';
import{Wallet,Plus,Trash2,Edit3,Check,X,Filter,Download,AlertCircle,Sun,Moon,Eye,EyeOff,Calendar,ChevronRight,MessageCircle,Phone,Info,LogOut,Search,ChevronDown}from'lucide-react';

// Этап 2.9: фильтр кириллицы для полей пароля. Убирает все кириллические символы
// (Ѐ-ӿ — основной блок, включая ё) перед записью в state. Используется
// в onChange всех password input — пароли в кириллице создают проблемы при логине
// с устройств с английской раскладкой / без переключателя языка.
// Бэкенд тоже валидирует на принятие символов — это defense-in-depth.
export const stripCyrillic = (s) => String(s == null ? '' : s).replace(/[Ѐ-ӿԀ-ԯ]/g, '');

export const THEMES = {
  dark: {
    bg: '#09090b',        // True dark slate background (Neo-Minimalist)
    bgCard: '#18181b',    // Very slight elevation
    bgInput: '#27272a',   // Inputs slightly lighter
    border: '#27272a',    // Crisp fine borders
    text: '#fafafa',      // High contrast crisp white
    textSec: '#a1a1aa',
    textMuted: '#71717a',
    placeholder: '#52525b',
    accent: '#10b981',    // Vibrant emerald for active states
    accentGreen: '#10b981',
    accentRed: '#ef4444',
    accentYellow: '#f59e0b',
    accentPurple: '#8b5cf6',
    accentOrange: '#f97316',
    accentCyan: '#06b6d4',
    scrollTrack: '#09090b',
    scrollThumb: '#27272a',
    shadow: 'none',       // Removed heavy shadows! Flat design.
    gradient: 'none',
    btnText: '#ffffff',
    activeFilter: '#27272a'
  },
  light: {
    bg: '#dde4e1',        // Darker, solid sage-gray background base
    bgCard: '#e7efed',    // Lighter than background (Elevation 1)
    bgInput: '#f1f7f5',   // Lightest layer (Interactive) - NOT pure white
    border: '#c9d6d2',    // Crisp, defined borders
    text: '#1a2622',      // Forest charcoal (no change)
    textSec: '#4a5e56',
    textMuted: '#798d85',
    placeholder: '#abbcb4',
    accent: '#0b543d',    // Velvet Green (no change)
    accentGreen: '#059669',
    accentRed: '#dc2626',
    accentYellow: '#d97706',
    accentPurple: '#7c3aed',
    accentOrange: '#a17438',    // Muted premium Bronze
    accentCyan: '#0891b2',
    scrollTrack: '#dde4e1',
    scrollThumb: '#c9d6d2',
    shadow: '0 1px 3px rgba(0, 0, 0, 0.06)', 
    gradient: 'none',
    btnText: '#ffffff',
    activeFilter: '#e7efed'
  }
};

// ═══ Генерация цветовой палитры организации из accent-цвета ═══
// Преобразует HEX → HSL для генерации оттенков
function hexToHSL(hex) {
  let r = parseInt(hex.slice(1,3),16)/255, g = parseInt(hex.slice(3,5),16)/255, b = parseInt(hex.slice(5,7),16)/255;
  const max = Math.max(r,g,b), min = Math.min(r,g,b), d = max - min;
  let h = 0, s = 0, l = (max+min)/2;
  if (d > 0) {
    s = l > 0.5 ? d/(2-max-min) : d/(max+min);
    if (max === r) h = ((g-b)/d + (g<b?6:0))/6;
    else if (max === g) h = ((b-r)/d+2)/6;
    else h = ((r-g)/d+4)/6;
  }
  return [Math.round(h*360), Math.round(s*100), Math.round(l*100)];
}

function hslToHex(h,s,l) {
  s/=100; l/=100;
  const a=s*Math.min(l,1-l);
  const f=n=>{const k=(n+h/30)%12;const c=l-a*Math.max(Math.min(k-3,9-k,1),-1);return Math.round(255*c).toString(16).padStart(2,'0')};
  return `#${f(0)}${f(8)}${f(4)}`;
}

// Набор предустановленных цветов для организаций
export const ORG_COLORS = [
  { id: 'emerald', hex: '#0b543d', label: 'Изумруд' },
  { id: 'sapphire', hex: '#1e3a5f', label: 'Сапфир' },
  { id: 'ruby', hex: '#7f1d1d', label: 'Рубин' },
  { id: 'amethyst', hex: '#4c1d95', label: 'Аметист' },
  { id: 'amber', hex: '#78350f', label: 'Янтарь' },
  { id: 'ocean', hex: '#134e4a', label: 'Океан' },
  { id: 'slate', hex: '#334155', label: 'Графит' },
  { id: 'rose', hex: '#881337', label: 'Роза' },
];

// Генерирует полную тему light+dark из одного accent-цвета
export function generateOrgTheme(accentHex) {
  const [h, s] = hexToHSL(accentHex || '#0b543d');

  return {
    light: {
      ...THEMES.light,
      accent: accentHex,
      bg: hslToHex(h, Math.max(8, s*0.15), 88),
      bgCard: hslToHex(h, Math.max(6, s*0.12), 92),
      bgInput: hslToHex(h, Math.max(5, s*0.1), 96),
      border: hslToHex(h, Math.max(8, s*0.15), 80),
      scrollTrack: hslToHex(h, Math.max(8, s*0.15), 88),
      scrollThumb: hslToHex(h, Math.max(8, s*0.15), 80),
      activeFilter: hslToHex(h, Math.max(6, s*0.12), 92),
    },
    dark: {
      ...THEMES.dark,
      accent: hslToHex(h, Math.min(70, s), 55),
      bgCard: hslToHex(h, Math.max(5, s*0.08), 10),
      bgInput: hslToHex(h, Math.max(5, s*0.1), 15),
      border: hslToHex(h, Math.max(5, s*0.1), 15),
      scrollThumb: hslToHex(h, Math.max(5, s*0.1), 15),
    },
  };
}

export const ThemeCtx=createContext(THEMES.light);
export const useTheme=()=>useContext(ThemeCtx);
export const VERSION='0.3a';

// НОВОЕ: Добавлен класс .hide-scroll для тонкого, изящного скроллбара
export function getCSS(t){return`
*{margin:0;padding:0;box-sizing:border-box}
body{font-family:'Inter',sans-serif;-webkit-font-smoothing:antialiased;-moz-osx-font-smoothing:grayscale;background:${t.bg};color:${t.text};min-height:100vh;transition:background 0.3s ease, color 0.3s ease;font-size:13px;letter-spacing:-0.01em;}
::-webkit-scrollbar{width:4px;height:4px}
::-webkit-scrollbar-track{background:${t.scrollTrack}}
::-webkit-scrollbar-thumb{background:${t.scrollThumb};border-radius:2px}
.hide-scroll::-webkit-scrollbar{height:0px;width:0px;}
input,select,textarea{font-family:'Inter',sans-serif;background:${t.bgInput};border:1px solid ${t.border};color:${t.text};padding:10px 14px;border-radius:6px;font-size:13px;outline:none;transition:border 0.2s ease, box-shadow 0.2s ease;width:100%}
input:focus,select:focus,textarea:focus{border-color:${t.accent};box-shadow:0 0 0 1px ${t.accent}}
input::placeholder,textarea::placeholder{color:${t.placeholder};font-weight:400}
select{cursor:pointer;appearance:none;background-image:url("data:image/svg+xml;charset=UTF-8,%3csvg xmlns='http://www.w3.org/2000/svg' viewBox='0 0 24 24' fill='none' stroke='%2371717a' stroke-width='2' stroke-linecap='round' stroke-linejoin='round'%3e%3cpolyline points='6 9 12 15 18 9'%3e%3c/polyline%3e%3c/svg%3e");background-repeat:no-repeat;background-position:right 12px center;background-size:14px;padding-right:36px}
textarea{resize:vertical;min-height:80px}
button{font-family:'Inter',sans-serif;cursor:pointer;border:none;outline:none;transition:all 0.15s ease;font-weight:500;font-size:13px;letter-spacing:-0.01em}
button:hover{opacity:0.85}
button:active{opacity:0.7;transform:translateY(1px)}
.mono{font-family:'JetBrains Mono',monospace;letter-spacing:-0.02em}

/* GLOBAL 100M SAAS OVERRIDES: Stripping bubbly radii and heavy shadows */
div, button, article, section { box-shadow: none !important; }
div[style*="box-shadow"], button[style*="box-shadow"] { box-shadow: none !important; }
div[style*="border-radius: 16px"], div[style*="border-radius: 18px"], div[style*="border-radius: 20px"], div[style*="border-radius: 14px"], div[style*="border-radius: 12px"], div[style*="border-radius: 10px"] { border-radius: 8px !important; }
button[style*="border-radius: 10px"], button[style*="border-radius: 12px"] { border-radius: 6px !important; }
/* Inject 1px hard border for definition where shadows used to be */
div[style*="box-shadow"] { border: 1px solid ${t.border} !important; }

option{background:${t.bgCard};color:${t.text}}
@media(max-width:768px){
  body{font-size:12px}
  /* Мобильная адаптация модалок и гридов */
  input,select,textarea{padding:8px 10px;font-size:12px}
}
@media(max-width:480px){
  body{font-size:12px}
  /* Уменьшаем padding модалок на маленьких экранах */
  input,select,textarea{padding:8px 10px;font-size:12px}
}
`}

// ════════════ МУЛЬТИВАЛЮТНОСТЬ И ФОРМАТИРОВАНИЕ ════════════
export const CURRENCIES = [
  { id: 'KZT', symbol: '₸' },
  { id: 'RUB', symbol: '₽' },
  { id: 'USD', symbol: '$' },
  { id: 'EUR', symbol: '€' },
  { id: 'CNY', symbol: '¥' },
  { id: 'AED', symbol: 'AED' },
  { id: 'GBP', symbol: '£' },
  { id: 'TRY', symbol: '₺' },
  { id: 'UZS', symbol: 'сўм' },
  { id: 'KGS', symbol: 'сом' },
  { id: 'TJS', symbol: 'SM' },
  { id: 'BYN', symbol: 'Br' },
  { id: 'UAH', symbol: '₴' },
  { id: 'GEL', symbol: '₾' },
  { id: 'AZN', symbol: '₼' },
  { id: 'AMD', symbol: '֏' },
  { id: 'MDL', symbol: 'L' },
  { id: 'TMT', symbol: 'TMT' },
];
// Label берётся из S().currencies[id] — поддерживает мультиязычность
export const getCurrencyLabel = (id) => S().currencies[id] || id;

export let currentCurrencySymbol = '₸';
export const setCurrencySymbol = (sym) => { currentCurrencySymbol = sym; };

const LANG_LOCALES = { ru: 'ru-RU', kz: 'kk-KZ', en: 'en-US' };
export const getLocale = () => LANG_LOCALES[getLang()] || 'ru-RU';

export function fmt(value) {
  if (value == null || isNaN(value)) return `0 ${currentCurrencySymbol}`;
  const val = typeof value === 'string' ? parseFloat(value) : value;
  return new Intl.NumberFormat(getLocale(), {minimumFractionDigits:2, maximumFractionDigits:2}).format(val) + ' ' + currentCurrencySymbol;
}

// ═══ Утилиты ═══
export const uid=()=>Math.random().toString(36).slice(2,11)+Date.now().toString(36);

// ═══ Outbox удалён (2026-05-26): откат Task #48 + Task #54 ═══
// КОНТЕКСТ: UX-проблема — silent retry создавал ловушку. Юзер не видел badge,
// создавал дубликаты при offline. Фактический инцидент в smoke-тесте: 2 pending items
// висели 6+ минут, юзер не понимал что произошло.
// НОВОЕ ПОВЕДЕНИЕ (Phase A): handleSyncError alert'ит юзера на ЛЮБОЙ ошибке
// (offline/network/5xx/4xx). Без локальной очереди ретраев.
// Phase B (отдельный коммит): callsites переписаны на await+rollback —
// UI не показывает операцию если backend не подтвердил.
// Cleanup orphan: App.js на mount чистит localStorage 'inmonex_sync_outbox_v1'.
// Removed exports: outbox, OUTBOX_BACKOFF_DELAYS_MS, OUTBOX_MAX_ATTEMPTS.
// Removed file: src/outboxDrain.js (stub оставлен для одного reload cycle).
//
// Если в будущем понадобится outbox — отдельный stage. Тогда правильное решение:
// blocking UI submit + spinner до API success, а не silent background retry.

// P1.6 (2026-05-21): Группировка счетов «Активные / Скрытые» + confirmation модалка.
// Раньше выпадающий список был одной плоской мешаниной, и бухгалтер случайно зачислял
// поступления на скрытый счёт (incident: Абдикаримов 96k на Халык → кассовый разрыв).
// Юзер прямо запросил: «в выпадающем списке выпадает список сначала строка доступные
// счета и там активные счет, и потом снизу скрытые счета. По умолчанию вверху всегда
// только активные. При выборе скрытого — модалка подтверждения, особенно при поступлении».
//
// AccountOptionsGrouped: возвращает JSX-фрагмент <optgroup>...для нативного <select>.
// Скрытые помечены 🔒 в названии. Активные всегда сверху.
// Использование:
//   <select value={accId} onChange={e => handleAccountChange(e.target.value)}>
//     <AccountOptionsGrouped accounts={data.accounts} />
//   </select>
export function AccountOptionsGrouped({ accounts, includeArchived = false }) {
  const list = (accounts || []).filter(a => includeArchived || !a.archived);
  const active = list.filter(a => !a.isHidden);
  const hidden = list.filter(a => a.isHidden);
  return (
    <React.Fragment>
      {active.length > 0 && (
        <optgroup label="— Активные счета —">
          {active.map(a => <option key={a.id} value={a.id}>{a.name}</option>)}
        </optgroup>
      )}
      {hidden.length > 0 && (
        <optgroup label="— Скрытые счета (забалансовые) —">
          {hidden.map(a => <option key={a.id} value={a.id}>🔒 {a.name}</option>)}
        </optgroup>
      )}
      {active.length === 0 && hidden.length === 0 && (
        <option value="">Нет доступных счетов</option>
      )}
    </React.Fragment>
  );
}

// confirmHiddenAccountChoice: window.confirm с понятным сообщением.
// Возвращает true если юзер подтвердил выбор скрытого, false если отказался.
// mode: 'income' (поступление — критично), 'expense', 'general'.
// Использование:
//   const handleAccountChange = (val) => {
//     const acc = (data.accounts || []).find(a => a.id === val);
//     if (acc?.isHidden && !confirmHiddenAccountChoice(acc.name, 'income')) return;
//     setAccId(val);
//   };
export function confirmHiddenAccountChoice(accountName, mode = 'general') {
  const messages = {
    income: `⚠️ Внимание: вы выбрали скрытый (забалансовый) счёт «${accountName}».\n\nПоступления на скрытые счета НЕ учитываются в фондах и могут создать путаницу в отчётах.\n\nТочно продолжить?`,
    expense: `⚠️ Внимание: вы выбрали скрытый счёт «${accountName}».\n\nРасходы со скрытых счетов не отражаются в фондах. Точно продолжить?`,
    general: `Вы выбрали скрытый счёт «${accountName}». Точно продолжить?`,
  };
  return window.confirm(messages[mode] || messages.general);
}
export const fmtDate=d=>new Date(d).toLocaleDateString(getLocale(),{day:'2-digit',month:'2-digit',year:'numeric',hour:'2-digit',minute:'2-digit'});
export const fmtShort=d=>new Date(d).toLocaleTimeString(getLocale(),{hour:'2-digit',minute:'2-digit'})+' '+new Date(d).toLocaleDateString(getLocale(),{day:'2-digit',month:'2-digit'});
export const fmtDay=d=>new Date(d).toISOString().slice(0,10);
export const diffDays=(d1,d2)=>{
  const t1=new Date(d1).setHours(0,0,0,0);
  const t2=new Date(d2).setHours(0,0,0,0);
  return Math.round((t1-t2)/(1000*60*60*24));
};
export const COLORS=['#5b9cf6','#f06caa','#f0b429','#2dd4a0','#9f85f7','#f28c3c','#28c0d8','#f06868','#c084fc','#84cc16'];
// Роли: лейблы из strings.js (Proxy безопасен для объекта — только чтение по ключу)
export const ROLE_LABELS=new Proxy({},{get(_,key){return S().roles[key]||key}});

export const PERM_LABELS = {
  manageAccounts: 'Счета',
  manageFunds: 'Фонды',
  manageCategories: 'Категории',
  manageContractors: 'Контрагенты',
  manageOperations: 'Операции',
  manageDebts: 'Обязательства'
};

export const ROLE_PERMS = {
  owner: { 
    all: true, 
    seeHistory: 'all',
    manageAccounts: true,
    manageFunds: true,
    manageCategories: true,
    manageContractors: true,
    manageOperations: true,
    manageDebts: true
  },
  admin: {
    manageContractors: true,
    manageDebts: true,
    manageOperations: true,
    seeHistory: 'own'
  },
  accountant: { 
    manageAccounts: true,
    manageFunds: true,
    manageCategories: true,
    manageContractors: true, 
    manageOperations: true, 
    manageDebts: true,
    seeHistory: 'own' 
  },
  viewer: { readOnly: true }
};



// Месяцы: функции-геттеры для мультиязычности (массивы нельзя через Proxy — ломается .map)
export const getMonthNames=()=>S().months;
export const getMonthShort=()=>S().monthsShort;

export function TengeIcon({size=18,color='currentColor'}){return<svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke={color} strokeWidth="2.5" strokeLinecap="round"><line x1="4" y1="6" x2="20" y2="6"/><line x1="4" y1="10" x2="20" y2="10"/><line x1="12" y1="10" x2="12" y2="20"/></svg>}

// Числовой инпут с поддержкой до 2 знаков после запятой (копейки/тиыны).
// Принимает "." и "," как разделитель, нормализует в "." в onChange.
// Отображение форматируется по локали (ru: "12 345,67" / en: "12,345.67").
export function NumInput({value,onChange,placeholder,style}){
  // Форматтер/разделители по текущей локали
  const formatParts = (n) => {
    try { return new Intl.NumberFormat(getLocale()).formatToParts(n); }
    catch { return []; }
  };
  const decimalSep = (formatParts(1.1).find(p => p.type === 'decimal') || {}).value || ',';
  const groupSep   = (formatParts(1000).find(p => p.type === 'group')   || {}).value || ' ';
  // Локаль-агностичный форматер внутри (строим сами, чтобы сохранять хвостовой "."/"0"):
  const formatDisplay = (raw) => {
    if (!raw) return '';
    if (raw === '.') return '0' + decimalSep;
    const [intPart = '0', decPart] = raw.split('.');
    const intNum = parseInt(intPart || '0', 10) || 0;
    const intFmt = new Intl.NumberFormat(getLocale()).format(intNum);
    return decPart !== undefined ? (intFmt + decimalSep + decPart) : intFmt;
  };
  // value может прийти как number/string/''. Нормализуем к строке вида "12345.67"
  const toRaw = (v) => {
    if (v === null || v === undefined || v === '') return '';
    const s = String(v).replace(',', '.');
    return /^\d+(\.\d*)?$/.test(s) ? s : (isNaN(parseFloat(s)) ? '' : String(parseFloat(s)));
  };
  const [d, setD] = useState(formatDisplay(toRaw(value)));
  // Если родитель сбросил value (пустая строка / null / 0-reset), синхронизируемся.
  // Если value — число и совпадает с тем, что мы уже ввели, НЕ перетираем: это может
  // затирать "12345." во время ввода.
  useEffect(() => {
    if (value === '' || value === null || value === undefined) { setD(''); return; }
    // Соответствует ли текущий displayed корректному value?
    const currentRaw = d.replace(new RegExp('\\' + groupSep, 'g'), '').replace(decimalSep, '.');
    if (parseFloat(currentRaw) !== parseFloat(String(value).replace(',', '.'))) {
      setD(formatDisplay(toRaw(value)));
    }
  }, [value]);
  const h = e => {
    let raw = e.target.value;
    // 1) Снимаем группировочный разделитель (nbsp / пробел / запятая для en-US).
    //    Делаем это ДО нормализации десятичного, иначе при локали с group=','
    //    запятая-тысячи превратится в ложную десятичную точку.
    if (groupSep) raw = raw.split(groupSep).join('');
    // Доп. защита: снимаем все виды пробелов (regular space, nbsp, narrow nbsp и т.п.)
    raw = raw.replace(/\s/g, '');
    // 2) Нормализуем локальный десятичный разделитель в "."
    if (decimalSep && decimalSep !== '.') {
      raw = raw.split(decimalSep).join('.');
    }
    // 3) Пользователь может ввести "." или "," — принимаем оба как десятичный,
    //    но только если это не group-separator текущей локали (т.е. запятая
    //    в en-US мы уже сняли как group на шаге 1, сюда уже не придёт).
    if (!raw.includes('.') && raw.includes(',')) {
      raw = raw.replace(',', '.');
    }
    // 4) Оставляем только цифры и точку
    raw = raw.replace(/[^\d.]/g, '');
    // 5) Только одна точка (первая)
    const i = raw.indexOf('.');
    if (i !== -1) {
      raw = raw.slice(0, i + 1) + raw.slice(i + 1).replace(/\./g, '');
      // Ограничиваем до 2 знаков после запятой
      const [a, b = ''] = raw.split('.');
      raw = a + '.' + b.slice(0, 2);
    }
    // 6) Удаляем ведущие нули в целой части, но сохраняем "0" и "0.xx"
    if (raw.includes('.')) {
      const [a, b] = raw.split('.');
      raw = (a === '' ? '0' : String(parseInt(a, 10) || 0)) + '.' + b;
    } else if (raw.length > 1 && raw.startsWith('0')) {
      raw = String(parseInt(raw, 10) || 0);
    }
    if (!raw) { setD(''); onChange(''); return; }
    setD(formatDisplay(raw));
    onChange(raw); // строка с точкой — parseFloat на стороне потребителя корректно парсит
  };
  return <input value={d} onChange={h} placeholder={placeholder||S().numInputPlaceholder} style={style} inputMode="decimal" />;
}

export function getDayBounds(day){const s=new Date(day+'T00:00:00');const e=new Date(day+'T23:59:59.999');return{start:s,end:e}}
export function isDateInRange(value,dateFrom,dateTo){
  const time=new Date(value).getTime();if(Number.isNaN(time))return false;
  if(dateFrom&&!dateTo){const{start,end}=getDayBounds(dateFrom);return time>=start.getTime()&&time<=end.getTime()}
  if(dateFrom){const{start}=getDayBounds(dateFrom);if(time<start.getTime())return false}
  if(dateTo){const{end}=getDayBounds(dateTo);if(time>end.getTime())return false}
  return true;
}

// ═══ Период (для фильтров суммы по контрагентам/категориям) ═══
// Возвращает {start, end} для одного из пресетов: '30d' | '3m' | '6m' | '1y' | 'all' | 'custom'
export function getPeriodRange(period, dateFrom, dateTo){
  const now = new Date();
  const end = new Date(now.getFullYear(), now.getMonth(), now.getDate(), 23, 59, 59, 999);
  if(period === 'custom'){
    const start = dateFrom ? new Date(dateFrom + 'T00:00:00') : null;
    const customEnd = dateTo ? new Date(dateTo + 'T23:59:59.999') : end;
    return {start, end: customEnd};
  }
  if(period === 'all') return {start: null, end};
  let start = new Date(now);
  if(period === '30d') start.setDate(now.getDate() - 30);
  else if(period === '3m') start.setMonth(now.getMonth() - 3);
  else if(period === '6m') start.setMonth(now.getMonth() - 6);
  else if(period === '1y') start.setFullYear(now.getFullYear() - 1);
  else return {start: null, end};
  start.setHours(0,0,0,0);
  return {start, end};
}

// Проверка попадания даты в период
export function isInPeriod(value, range){
  if(!range || !range.start) return true;
  const t = new Date(value).getTime();
  if(Number.isNaN(t)) return false;
  return t >= range.start.getTime() && t <= range.end.getTime();
}

// Модалка выбора своего периода (2 поля: с / по). Адаптив: на мобилке — во всю ширину.
function CustomPeriodModal({initialFrom, initialTo, onApply, onClose}){
  const t = useTheme();
  const labels = (S().periodFilter) || {from:'с', to:'по', custom:'Свой период', apply:'Применить', reset:'Сбросить', cancel:'Отмена', pickDates:'Выберите период'};
  const [from, setFrom] = useState(initialFrom || '');
  const [to, setTo] = useState(initialTo || '');
  const [err, setErr] = useState('');
  const fromRef = useRef(null);
  const toRef = useRef(null);

  const apply = () => {
    setErr('');
    if(!from || !to){ setErr(labels.errBoth || 'Заполните обе даты'); return; }
    if(new Date(from) > new Date(to)){ setErr(labels.errOrder || 'Дата «с» должна быть раньше «по»'); return; }
    onApply(from, to);
  };
  const reset = () => { setFrom(''); setTo(''); setErr(''); };

  const fieldStyle = {
    width:'100%',
    padding:'12px 14px',
    borderRadius:10,
    background:t.bgInput,
    color:t.text,
    border:`1px solid ${t.border}`,
    fontSize:15,
    outline:'none',
    boxSizing:'border-box',
  };
  const iconBtn = {
    width:44, height:44, borderRadius:10, background:t.bgInput, color:t.textSec,
    border:`1px solid ${t.border}`, display:'inline-flex', alignItems:'center', justifyContent:'center',
    flexShrink:0, cursor:'pointer'
  };

  return (
    <div
      style={{position:'fixed', inset:0, background:'rgba(0,0,0,.55)', zIndex:1200, display:'flex', alignItems:'center', justifyContent:'center', padding:16}}
      onClick={onClose}
    >
      <div
        onClick={e=>e.stopPropagation()}
        style={{
          background:t.bgCard,
          border:`1px solid ${t.border}`,
          borderRadius:16,
          padding:'20px 20px 16px',
          width:'100%',
          maxWidth:440,
          maxHeight:'90vh',
          overflowY:'auto',
          boxShadow:'0 20px 60px rgba(0,0,0,.4)',
          boxSizing:'border-box',
        }}
      >
        <div style={{display:'flex', justifyContent:'space-between', alignItems:'center', marginBottom:18}}>
          <div style={{fontSize:17, fontWeight:800, color:t.text}}>{labels.pickDates || 'Выберите период'}</div>
          <button onClick={onClose} style={{background:'#fee2e2', border:'2px solid #b91c1c', borderRadius:10, padding:5, color:'#b91c1c', cursor:'pointer', display:'inline-flex', alignItems:'center', justifyContent:'center'}}><X size={18}/></button>
        </div>

        <div style={{display:'grid', gap:14, marginBottom:err?10:18}}>
          <div>
            <label style={{fontSize:12, color:t.textMuted, fontWeight:700, marginBottom:6, display:'block'}}>{labels.from || 'С'}</label>
            <div style={{display:'flex', gap:8}}>
              <input ref={fromRef} type="date" value={from} onChange={e=>setFrom(e.target.value)} style={fieldStyle}/>
              <button type="button" onClick={()=>{ if(fromRef.current?.showPicker) fromRef.current.showPicker(); else fromRef.current?.focus(); }} style={iconBtn} title={labels.from}><Calendar size={18}/></button>
            </div>
          </div>
          <div>
            <label style={{fontSize:12, color:t.textMuted, fontWeight:700, marginBottom:6, display:'block'}}>{labels.to || 'По'}</label>
            <div style={{display:'flex', gap:8}}>
              <input ref={toRef} type="date" value={to} onChange={e=>setTo(e.target.value)} style={fieldStyle}/>
              <button type="button" onClick={()=>{ if(toRef.current?.showPicker) toRef.current.showPicker(); else toRef.current?.focus(); }} style={iconBtn} title={labels.to}><Calendar size={18}/></button>
            </div>
          </div>
        </div>

        {err && (
          <div style={{padding:'10px 12px', background:'#fee2e2', color:'#b91c1c', borderRadius:10, fontSize:13, fontWeight:600, marginBottom:14, border:'1px solid #fecaca'}}>{err}</div>
        )}

        <div style={{display:'flex', gap:10, flexWrap:'wrap'}}>
          <button
            onClick={reset}
            style={{flex:'1 1 auto', minWidth:110, padding:'12px 14px', borderRadius:10, background:t.bgInput, color:t.textSec, border:`1px solid ${t.border}`, fontSize:14, fontWeight:600, cursor:'pointer'}}
          >{labels.reset || 'Сбросить'}</button>
          <button
            onClick={apply}
            style={{flex:'2 1 auto', minWidth:130, padding:'12px 14px', borderRadius:10, background:t.accent, color:t.btnText||'#fff', border:'none', fontSize:14, fontWeight:700, cursor:'pointer'}}
          >{labels.apply || 'Применить'}</button>
        </div>
      </div>
    </div>
  );
}

// Универсальный селектор периода.
// На широком экране — ряд кнопок-чипов; на узком (isCompact=true) — выпадающий список.
// При выборе «Свой период» открывается адаптивная модалка с двумя полями дат.
export function PeriodFilter({period, setPeriod, dateFrom, setDateFrom, dateTo, setDateTo, label, isCompact}){
  const t = useTheme();
  const [modalOpen, setModalOpen] = useState(false);
  const labels = (S().periodFilter) || {
    label: 'Период:', d30: '30 дней', m3: '3 месяца', m6: '6 месяцев', y1: 'Год', all: 'Всё время', custom: 'Свой период', from: 'с', to: 'по'
  };

  // Набор пресетов: 30 дней, пол года, год, свой период
  const presets = [
    {val:'30d', label:labels.d30},
    {val:'6m',  label:labels.m6},
    {val:'1y',  label:labels.y1},
    {val:'custom', label:labels.custom},
  ];

  // Обработчик клика по пресету. Для «custom» открываем модалку.
  const handlePick = (val) => {
    if (val === 'custom') {
      setModalOpen(true);
    } else {
      setPeriod(val);
    }
  };

  // Для подсказки когда «custom» активен — показываем диапазон коротко
  const fmtShortDate = (iso) => {
    if (!iso) return '';
    const d = new Date(iso);
    if (Number.isNaN(d.getTime())) return iso;
    return d.toLocaleDateString(getLocale(), {day:'2-digit', month:'2-digit', year:'2-digit'});
  };
  const customHint = period === 'custom' && (dateFrom || dateTo)
    ? `${fmtShortDate(dateFrom)} — ${fmtShortDate(dateTo)}`
    : null;

  const modal = modalOpen && (
    <CustomPeriodModal
      initialFrom={dateFrom}
      initialTo={dateTo}
      onClose={() => setModalOpen(false)}
      onApply={(f, to) => {
        setDateFrom(f);
        setDateTo(to);
        setPeriod('custom');
        setModalOpen(false);
      }}
    />
  );

  if (isCompact) {
    // Компактный вид — выпадающий список
    const sel = {padding:'8px 12px',borderRadius:8,background:t.bgInput,color:t.text,border:`1px solid ${t.border}`,fontSize:13,cursor:'pointer'};
    return (
      <div style={{display:'flex',alignItems:'center',flexWrap:'wrap',gap:8}}>
        <span style={{fontSize:12,color:t.textMuted,fontWeight:600}}>{label || labels.label}</span>
        <select value={period} onChange={e => handlePick(e.target.value)} style={sel}>
          {presets.map(p => <option key={p.val} value={p.val}>{p.label}</option>)}
        </select>
        {customHint && (
          <button onClick={() => setModalOpen(true)} style={{fontSize:12, padding:'6px 10px', borderRadius:8, background:`${t.accent}18`, color:t.accent, border:`1px solid ${t.accent}40`, cursor:'pointer', fontWeight:700}}>{customHint}</button>
        )}
        {modal}
      </div>
    );
  }

  // Широкий вид — ряд кнопок-чипов
  return (
    <div style={{display:'flex',alignItems:'center',flexWrap:'wrap',gap:8}}>
      {label && <span style={{fontSize:12,color:t.textMuted,fontWeight:600}}>{label}</span>}
      <div style={{display:'inline-flex',gap:4,background:t.bgInput,borderRadius:10,padding:3,flexWrap:'wrap'}}>
        {presets.map(p => {
          const active = period === p.val;
          const displayLabel = (p.val === 'custom' && active && customHint) ? customHint : p.label;
          return (
            <button
              key={p.val}
              onClick={() => handlePick(p.val)}
              style={{
                padding:'7px 13px',
                borderRadius:8,
                background: active ? t.bgCard : 'transparent',
                color: active ? t.accent : t.textMuted,
                fontWeight: active ? 700 : 500,
                fontSize: 12.5,
                border:'none',
                cursor:'pointer',
                boxShadow: active ? t.shadow : 'none',
                transition:'all 0.15s',
                whiteSpace:'nowrap',
              }}
            >{displayLabel}</button>
          );
        })}
      </div>
      {modal}
    </div>
  );
}

export function DateField({value,onChange,placeholder,width=170,active=false}){
  const t=useTheme();const ref=useRef(null);
  return<div style={{display:'flex',alignItems:'center',gap:6}}>
    <input ref={ref} type="date" value={value} onChange={e=>onChange(e.target.value)} title={placeholder} style={{width,...(active?{background:t.activeFilter,borderColor:t.accent}:{})}}/>
    <button type="button" onClick={()=>{if(ref.current?.showPicker)ref.current.showPicker();else ref.current?.focus()}} title={placeholder} style={{width:42,height:42,borderRadius:10,background:active?t.activeFilter:t.bgInput,color:active?t.accent:t.textSec,border:`1px solid ${active?t.accent:t.border}`,display:'inline-flex',alignItems:'center',justifyContent:'center',flexShrink:0}}><Calendar size={18}/></button>
  </div>;
}

export function ContactModal({onClose}){const t=useTheme();
  const ls={padding:14,background:t.bgInput,border:`1px solid ${t.border}`,borderRadius:12,display:'flex',alignItems:'center',gap:10,textDecoration:'none',color:t.text};
  return<div style={{position:'fixed',inset:0,background:'rgba(0,0,0,.5)',zIndex:220,display:'flex',alignItems:'center',justifyContent:'center'}} onClick={onClose}>
    <div onClick={e=>e.stopPropagation()} style={{background:t.bgCard,border:`1.5px solid ${t.border}`,borderRadius:16,padding:28,width:420,maxWidth:'90vw',boxShadow:'0 20px 60px rgba(0,0,0,.3)'}}>
      <div style={{display:'flex',justifyContent:'space-between',alignItems:'center',marginBottom:18}}><div style={{fontSize:18,fontWeight:700}}>{S().contacts.title}</div><button onClick={onClose} style={{background:'#fee2e2',border:'2px solid #b91c1c',borderRadius:10,padding:5,color:'#b91c1c',cursor:'pointer'}}><X size={18}/></button></div>
      <div style={{display:'grid',gap:12}}>
        <a href="https://t.me/SuperConqueror" target="_blank" rel="noreferrer" style={ls}><MessageCircle size={18} color={t.accent}/><div><div style={{fontSize:12,color:t.textMuted,marginBottom:2}}>{S().contacts.telegram}</div><div style={{fontSize:15,fontWeight:700}}>@SuperConqueror</div></div></a>
        <a href="https://wa.me/77019200287" target="_blank" rel="noreferrer" style={ls}><Phone size={18} color={t.accentGreen}/><div><div style={{fontSize:12,color:t.textMuted,marginBottom:2}}>{S().contacts.whatsapp}</div><div style={{fontSize:15,fontWeight:700}}>+7 701 920 02 87</div></div></a>
      </div>
    </div>
  </div>;
}

// ═══ UI-компоненты ═══
export function Card({children,style,compact}){const t=useTheme();return<div style={{background:t.bgCard,border:`1.5px solid ${t.border}`,borderRadius:compact?10:14,padding:compact?14:22,boxShadow:t.shadow,...style}}>{children}</div>}
export function StatCard({icon:Icon,label,value,color,customIcon,compact}){const t=useTheme();return<Card style={{display:'flex',gap:compact?10:14,alignItems:'center',padding:compact?'12px 10px':'18px 20px'}}><div style={{width:compact?34:44,height:compact?34:44,borderRadius:compact?8:12,background:`${color}14`,display:'flex',alignItems:'center',justifyContent:'center',flexShrink:0}}>{customIcon||<Icon size={compact?16:20} color={color}/>}</div><div style={{minWidth:0}}><div style={{fontSize:compact?11:12,color:t.textMuted,marginBottom:2,fontWeight:500,lineHeight:1.2}}>{label}</div><div className="mono" style={{fontSize:compact?14:18,fontWeight:700,color}}>{value}</div></div></Card>}
export function Btn({children,onClick,variant='primary',style,disabled}){const t=useTheme();const s={primary:{background:t.accent,color:t.btnText,fontWeight:700},danger:{background:`${t.accentRed}14`,color:t.accentRed,fontWeight:700},ghost:{background:'transparent',color:t.textSec},success:{background:t.accentGreen,color:t.btnText,fontWeight:700},warn:{background:`${t.accentOrange}14`,color:t.accentOrange,fontWeight:700}};return<button disabled={disabled} onClick={onClick} style={{padding:'9px 18px',borderRadius:10,fontSize:14,display:'inline-flex',alignItems:'center',gap:6,opacity:disabled?.35:1,...s[variant],...style}}>{children}</button>}
export function PageHeader({title,sub,right}){const t=useTheme();return<div style={{display:'flex',justifyContent:'space-between',alignItems:'flex-start',marginBottom:26,flexWrap:'wrap',gap:14}}><div><h1 style={{fontSize:22,fontWeight:800}}>{title}</h1>{sub&&<div style={{fontSize:14,color:t.textMuted,marginTop:4}}>{sub}</div>}</div>{right}</div>}
export function TabSwitch({tabs,active,onChange}){const t=useTheme();return<div style={{display:'inline-flex',gap:3,background:t.bgInput,borderRadius:12,padding:4,flexWrap:'wrap'}}>{tabs.map(tb=><button key={tb.id} onClick={()=>onChange(tb.id)} style={{display:'flex',alignItems:'center',gap:5,padding:'8px 16px',borderRadius:9,background:active===tb.id?t.bgCard:'transparent',color:active===tb.id?t.accent:t.textMuted,fontWeight:active===tb.id?700:400,fontSize:13,boxShadow:active===tb.id?t.shadow:'none'}}>{tb.icon&&<tb.icon size={14}/>}{tb.label}</button>)}</div>}
export function Empty(){const t=useTheme();return<div style={{color:t.textMuted,textAlign:'center',padding:40,fontSize:14}}>{S().empty}</div>}
export function Confirm({msg,onYes,onNo,extra}){const t=useTheme();return <div style={{position:'fixed',inset:0,background:'rgba(0,0,0,.5)',zIndex:1500,display:'flex',alignItems:'center',justifyContent:'center'}} onClick={onNo}><div onClick={e=>e.stopPropagation()} style={{background:t.bgCard,border:`1px solid ${t.border}`,borderRadius:16,padding:28,width:420,maxWidth:'90vw',boxShadow:'0 20px 60px rgba(0,0,0,.3)'}}><div style={{fontSize:16,fontWeight:700,marginBottom:18}}>{msg}</div>{extra}<div style={{display:'flex',gap:10,justifyContent:'flex-end',marginTop:extra?20:0}}><Btn variant="ghost" onClick={onNo}>{S().confirmDialog.cancel}</Btn><Btn variant="danger" onClick={onYes}>{S().confirmDialog.confirm}</Btn></div></div></div>}

// ═══ Audit ═══
export const createAuditLogEntry = (user, data = {}) => {
  const d = data || {};
  return {
    id: uid(),
    uid: user?.id || 'system',
    uName: user?.name || 'Система',
    uRole: user?.role || 'system',
    date: new Date().toISOString(),
    type: d.type || 'UNKNOWN',
    desc: d.desc || '',
    before: d.before || null,
    after: d.after || null,
    details: d.details || null
  };
};

// Stage 21.5 (2026-05-14): module-level apiSync reference для addAuditLog.
// App.js регистрирует apiSync через _setApiSyncRef после useApiSync(). Так addAuditLog
// получает доступ к syncCreateAuditEntry без необходимости менять signature (32 callsites).
let _apiSyncRef = null;
export function _setApiSyncRef(syncObj) { _apiSyncRef = syncObj || null; }

export const addAuditLog = (uOrg, user, data) => {
  if (typeof uOrg !== 'function') return null;
  const entry = createAuditLogEntry(user, data);
  uOrg(p => ({
    ...p,
    auditLog: [entry, ...(p.auditLog || [])].slice(0, 1000)
  }));
  // Stage 21.5: parallel REST POST для немедленной персистенции в AuditLog таблицу.
  // Frontend optimistic update через uOrg остаётся primary path; REST идёт фоном.
  // Без REST вызова entry попал бы в БД только через bulk PUT /sync/org (saveOrg),
  // а после 21.6 cut JSON он бы вообще не сохранился в БД для frontend-only entries.
  if (_apiSyncRef && typeof _apiSyncRef.syncCreateAuditEntry === 'function') {
    try { _apiSyncRef.syncCreateAuditEntry(entry); } catch (e) { /* silent — не блокируем UI */ }
  }
  return entry;
};

// ═══ Списание баланса счёта с фондов при удалении «вместе с деньгами» (2026-06-10) ═══
// Раскидывает amount по фондам по их %; если доля > баланса фонда — берём сколько есть,
// недостачу каскадом списываем с фонда(ов) с наибольшим остатком. Ни один фонд не в минус.
// При сверке 0 сумма фондов >= amount, поэтому remaining всегда 0.
// Возвращает { breakdown:[{fundId,fundName,amount}], remaining }.
export function computeFundWriteoff(amount, funds) {
  const r2 = x => Math.round(x * 100) / 100;
  const X = r2(amount);
  const list = (funds || []).filter(f => f && f.id);
  const bal = {}, deduct = {};
  list.forEach(f => { bal[f.id] = r2(f.balance || 0); deduct[f.id] = 0; });
  list.forEach(f => {
    const share = r2(X * (f.percent || 0) / 100);
    const take = Math.min(share, bal[f.id]);
    deduct[f.id] = r2(take);
    bal[f.id] = r2(bal[f.id] - take);
  });
  let remaining = r2(X - r2(list.reduce((s, f) => s + deduct[f.id], 0)));
  let guard = 0;
  while (remaining > 0.005 && guard++ < 10000) {
    const sorted = list.filter(f => bal[f.id] > 0.005).sort((a, b) => bal[b.id] - bal[a.id]);
    if (!sorted.length) break;
    const f = sorted[0];
    const take = Math.min(remaining, bal[f.id]);
    deduct[f.id] = r2(deduct[f.id] + take);
    bal[f.id] = r2(bal[f.id] - take);
    remaining = r2(remaining - take);
  }
  const breakdown = list.filter(f => deduct[f.id] > 0).map(f => ({ fundId: f.id, fundName: f.name, amount: deduct[f.id] }));
  return { breakdown, remaining };
}

export function downloadAuditLog(data){const log=data.auditLog||[];const lines=['Дата\tТип\tДействие\tКто\tРоль\tДетали'];log.forEach(l=>lines.push(`${l.date}\t${l.type||''}\t${l.desc||''}\t${l.uName||''}\t${l.uRole||''}\t${l.details||''}`));const blob=new Blob([lines.join('\n')],{type:'text/tab-separated-values'});const u=URL.createObjectURL(blob);const a=document.createElement('a');a.href=u;a.download=`inmonex-history-${data.name}-${fmtDay(new Date())}.tsv`;a.click();URL.revokeObjectURL(u)}

// ═══ Excel export helper ═══
export function downloadHtmlExcel(filename,title,sections){const html=`<html xmlns="urn:schemas-microsoft-com:office:office" xmlns:x="urn:schemas-microsoft-com:office:spreadsheet" xmlns="http://www.w3.org/TR/REC-html40"><head><meta charset="UTF-8"><style>td,th{font-family:Arial,sans-serif;font-size:11pt;border:1px solid #ccc;padding:4px 8px}th{background:#f0f0f0;font-weight:bold}table{border-collapse:collapse}</style></head><body>${sections.join('')}</body></html>`;const blob=new Blob([html],{type:'application/vnd.ms-excel;charset=utf-8'});const u=URL.createObjectURL(blob);const a=document.createElement('a');a.href=u;a.download=filename;a.click();URL.revokeObjectURL(u)}


// ═══ Persistence ═══
// По умолчанию создаём пустой набор категорий — "Без категории" (uncat_exp)
// добавляется отдельно в normalizeOrgData. Пользователь сам заводит нужные категории.
export function createDefaultExpenseCategories(){return[]}

export function normalizeOrgData(raw){
  const funds=Array.isArray(raw.funds)?raw.funds:[];
  let expenseCategories=(Array.isArray(raw.expenseCategories)&&raw.expenseCategories.length>0?raw.expenseCategories:createDefaultExpenseCategories(funds)).filter(cat=>{if(!cat||typeof cat!=='object')return false;if(!cat.id||!cat.name)return false;return true});
  
  // Добавляем новые массивы, если их не было в старых сохранениях
  let incomeCategories=Array.isArray(raw.incomeCategories)?raw.incomeCategories:[];
  const contractors=Array.isArray(raw.contractors)?raw.contractors:[];

  // Task #58 (2026-05-25): убран synthetic injection 'uncat_inc'/'uncat_exp'.
  // Раньше materialize'или fake категории в массив — но их НЕ существовало в БД Category.
  // Это создавало конфликт: UI показывал «Без категории» как опцию, но если operation
  // имела category='uncat_exp' — Reports группировал под несуществующую категорию.
  // Теперь полагаемся на реальные категории из БД (через apiCategories overlay в App.js).
  // TxEditModal имеет <option value=""> для «Без категории» (DataTab.js:831) — это
  // legitimate выбор для operation.category=null. ContractorsTab и другие места читают
  // только реальные категории из массива.

  // === НОВОЕ: МИГРАЦИЯ ТРАНЗАКЦИЙ ===
  let transactions = Array.isArray(raw.transactions)?raw.transactions:[];
  // Stage 20b B.2 fix: backend syncDataMappers.operationToJson возвращает
  // expenseCategoryName / incomeCategoryName ='' (пустую строку). UI fallback
  // показывает 'Без категории'. Пере-резолвим name из categories arrays здесь.
  transactions = transactions.map(tx => {
    let next = tx;
    // Task #58 (2026-05-25): убрали fallback на synthetic 'uncat_inc'/'uncat_exp'.
    // Раньше операции без categoryId автоматически получали фейковый id который не
    // существовал в Category таблице → Reports группировал под несуществующую категорию.
    // Теперь оставляем category пустой/null — TxEditModal покажет «Без категории» опцию
    // (<option value="">) выбранной, юзер сможет явно поменять. Reports skip операции
    // без категории в по-категорийных группировках (или покажет отдельной строкой).
    // Если incomeCategoryId/expenseCategoryId есть но name пуст — orgDataView сделает
    // lookup в mergedCategories из apiCategories (authoritative).
    // Чанк A.5 (оставлен): fundName lookup из normalize. При load из localStorage
    // funds непустой → даёт быстрое имя до App.js overlay. orgDataView потом
    // подтвердит/перепишет через mergedFunds.
    if (!next.fundName && next.fundId) {
      const f = funds.find(f => f && f.id === next.fundId);
      if (f && f.name) next = { ...next, fundName: f.name };
    }
    if (!next.toFundName && next.toFundId) {
      const tf = funds.find(f => f && f.id === next.toFundId);
      if (tf && tf.name) next = { ...next, toFundName: tf.name };
    }
    return next;
  });

  const auditLog = Array.isArray(raw.auditLog) ? raw.auditLog : [];
  // Stage 20a Phase 2 / Чанк 20a.4b defensive: backend больше не пишет fundBalances в JSON
  // (источник — Fund.balance в БД, читается через App.js apiFunds view-layer). Но 87 read-sites
  // делают data.fundBalances[id] — если undefined → TypeError. Гарантируем пустой объект как
  // floor. orgDataView в App.js поверх него мерджит свежие balances из API.
  const fundBalances = (raw.fundBalances && typeof raw.fundBalances === 'object' && !Array.isArray(raw.fundBalances))
    ? raw.fundBalances
    : {};
  // Аналогично accountBalances (всё ещё нужны для Чанка 2C, UI читает напрямую):
  const accountBalances = (raw.accountBalances && typeof raw.accountBalances === 'object' && !Array.isArray(raw.accountBalances))
    ? raw.accountBalances
    : {};

  return{...raw,funds,expenseCategories,incomeCategories,contractors,transactions,auditLog,fundBalances,accountBalances}
}

export const META_KEY='finup_meta';

// ═══ localStorage-only (базовый уровень, всегда работает) ═══
function loadMetaLocal(){try{const r=localStorage.getItem(META_KEY);if(r)return JSON.parse(r)}catch(e){console.warn('loadMeta error:',e)}return null}
function saveMetaLocal(m){try{localStorage.setItem(META_KEY,JSON.stringify(m))}catch(e){console.warn('saveMeta error:',e)}}
function loadOrgLocal(id){try{const r=localStorage.getItem('finup_org_'+id);if(r)return normalizeOrgData(JSON.parse(r))}catch(e){console.warn('loadOrg error:',e)}return null}
function saveOrgLocal(id,d){try{localStorage.setItem('finup_org_'+id,JSON.stringify(normalizeOrgData(d)))}catch(e){console.warn('saveOrg error:',e)}}

// ═══ СЕРВЕР = источник истины, localStorage = кэш ═══
import * as api from './api';

// loadMeta: только тема и язык из localStorage (мгновенно при старте)
// Бизнес-данные (orgs, globalUsers) грузятся с сервера в App.js
export function loadMeta() { return loadMetaLocal(); }

// saveMeta: сохраняем тему/язык в localStorage + полную мету на сервер
export function saveMeta(m) {
  // В localStorage сохраняем только настройки устройства
  saveMetaLocal({ version: m.version, theme: m.theme, lang: m.lang });
  // Полная мета → на сервер
  if (api.getToken()) {
    api.syncMeta(m).catch(() => {});
  }
}

// loadOrg: возвращает кэш из localStorage (для быстрого старта)
// ВАЖНО: всегда перезагружайте данные с сервера через api.loadOrgFromServer()
export function loadOrg(id) { return loadOrgLocal(id); }

// saveOrg: кэш в localStorage + сервер (primary).
// Phase 9.1.5: onSaved(result) — опциональный коллбэк, проброс в api.syncOrg.
// Phase 10: expectedSyncedAt — версия для optimistic locking (берётся из polling
// manager-а в App.js, не из orgData._syncedAt которое stale после первого save).
// Stage 20a Phase 2 / Чанк 20a.4b (2026-05-13): cut fundBalances из server payload.
// Stage 20a Phase 2 / Чанк 20a.4c.2 (2026-05-13): cut funds metadata тоже.
// Stage 20a Phase 2 / Чанк 20a.6b (2026-05-14): cut accountBalances тоже.
// Балансы И metadata фондов и balances счетов — единственный источник правды
// в нормализованных таблицах Fund/Account. Backend пишет balance атомарно
// через 20a.3 (operations), metadata через REST endpoints (20a.4c.1 / 20a.5).
// UI читает оба через /api/orgs/:orgId/funds + /accounts (20a.4a + 20a.6a centralized view-layer).
// localStorage cache оставляем полным — fallback при offline.
// Stage 21.7 (2026-05-14): module-level hash check — skip PUT когда serverPayload
// идентичен последнему отправленному. Устраняет 409 race на ops creation: после
// всех cut'ов (transactions/accounts/debts/contractors/funds*/accountBalances/auditLog)
// body не меняется при операциях, hash идентичен → PUT не идёт → backend не получает
// stale expectedSyncedAt → нет 409.
const _lastSentPayloadHash = new Map(); // orgId → JSON.stringify(serverPayload)
export function saveOrg(id, d, onSaved, expectedSyncedAt) {
  const normalized = normalizeOrgData(d);
  saveOrgLocal(id, normalized); // кэш — полный, для offline fallback
  if (api.getToken()) {
    // Stage 21.6: cut auditLog (нормализованная AuditLog таблица).
    // Stage 21.7: cut transactions/accounts/debts/contractors (backend smart-merge
    // override для нормализованных орг уже подставляет их из БД). Body теперь
    // содержит только scalars + categories + theme/layout/users.
    // Stage 20b B.4 (2026-05-14): cut incomeCategories + expenseCategories. После B.2-B.3 Category таблица — единственный источник правды.
    const { fundBalances, funds, accountBalances, auditLog, transactions, accounts, debts, contractors, incomeCategories, expenseCategories, ...serverPayload } = normalized;

    // Hash skip — если ничего не изменилось, не отправляем PUT (избегаем 409 race).
    const hash = JSON.stringify(serverPayload);
    if (_lastSentPayloadHash.get(id) === hash) {
      if (typeof onSaved === 'function') onSaved({ skipped: true });
      return;
    }
    _lastSentPayloadHash.set(id, hash);

    api.syncOrg(id, serverPayload, onSaved, expectedSyncedAt).catch(() => {});
  }
}

// Экспортируем локальные версии для прямого доступа (миграции и т.д.)
export { loadMetaLocal, saveMetaLocal, loadOrgLocal, saveOrgLocal };
export function getInitialMeta(){
  const m=loadMeta();
  if(m&&m.version>=2)return m;
  // Миграция v1 → v2: добавляем globalUsers, orgColor
  if(m&&m.version>=1){
    const migrated = {...m, version:2, globalUsers: m.globalUsers || []};
    // Добавляем цвета организациям если нет
    migrated.orgs = (migrated.orgs||[]).map((o,i) => ({
      ...o,
      color: o.color || ORG_COLORS[i % ORG_COLORS.length].hex,
    }));
    // Миграция пользователей из орг в globalUsers
    if(migrated.globalUsers.length === 0) {
      const seen = new Map(); // login → globalUser
      for(const orgInfo of migrated.orgs) {
        const orgData = loadOrg(orgInfo.id);
        if(!orgData || !orgData.users) continue;
        for(const u of orgData.users) {
          if(!seen.has(u.login)) {
            seen.set(u.login, {
              id: u.id, login: u.login, password: u.password, name: u.name,
              orgAccess: [{ orgId: orgInfo.id, role: u.role, permissions: u.permissions || {} }]
            });
          } else {
            // Тот же логин — добавляем доступ к ещё одной орг
            seen.get(u.login).orgAccess.push({ orgId: orgInfo.id, role: u.role, permissions: u.permissions || {} });
          }
        }
      }
      migrated.globalUsers = [...seen.values()];
    }
    saveMeta(migrated);
    return migrated;
  }
  return{version:2,orgs:[],theme:'light',globalUsers:[]}
}

export function createOrgData(name,accounts,funds,adminUser){
  // Считаем общий начальный баланс из всех счетов
  const totalBalance = accounts.reduce((sum, a) => sum + (parseFloat(a.balance) || 0), 0);
  // Начальные балансы счетов
  const accountBalances = accounts.reduce((a, ac) => ({ ...a, [ac.id]: parseFloat(ac.balance) || 0 }), {});
  // Распределяем начальный баланс по фондам (по процентам)
  const fundBalances = funds.reduce((a, f) => {
    const pct = parseFloat(f.percent) || 0;
    return { ...a, [f.id]: totalBalance * pct / 100 };
  }, {});
  return normalizeOrgData({
    version:3,
    name,
    accounts,
    funds,
    users:[adminUser],
    transactions:[],
    fundBalances,
    accountBalances,
    auditLog:[],
    expenseCategories:createDefaultExpenseCategories(funds),
    incomeCategories:[],
    contractors:[]
  })
}

// ═══ Date/Month helpers ═══
export function monthKeyFromDate(date){return`${date.getFullYear()}-${String(date.getMonth()+1).padStart(2,'0')}`}
export function parseMonthKey(key){const[year,month]=key.split('-').map(Number);return new Date(year,month-1,1)}
export function shiftMonthKey(key,delta){const dt=parseMonthKey(key);dt.setMonth(dt.getMonth()+delta);return monthKeyFromDate(dt)}
export function startOfMonth(date){return new Date(date.getFullYear(),date.getMonth(),1)}
export function endOfMonth(date){return new Date(date.getFullYear(),date.getMonth()+1,0,23,59,59,999)}
export function formatMonthLabelFromKey(key){return parseMonthKey(key).toLocaleDateString(getLocale(),{month:'long',year:'numeric'})}
export function getMonthGridInfo(key){const first=parseMonthKey(key);const daysInMonth=new Date(first.getFullYear(),first.getMonth()+1,0).getDate();const now=new Date();const isCurrentMonth=first.getFullYear()===now.getFullYear()&&first.getMonth()===now.getMonth();const lastVisibleDay=isCurrentMonth?now.getDate():daysInMonth;const weeks=Math.ceil(lastVisibleDay/7);return{daysInMonth,isCurrentMonth,lastVisibleDay,weeks}}
export function getRange(pid){const n=new Date(),s=new Date();switch(pid){case'day':s.setHours(0,0,0,0);break;case'week':s.setDate(n.getDate()-7);break;case'month':s.setMonth(n.getMonth()-1);break;case'year':s.setFullYear(n.getFullYear()-1);break;case'2year':s.setFullYear(n.getFullYear()-2);break;default:s.setFullYear(2000)}return{start:s,end:n}}
// Периоды фильтрации — лейблы из strings.js
export const PERIODS=[{id:'day',get label(){return S().periods.day}},{id:'week',get label(){return S().periods.week}},{id:'month',get label(){return S().periods.month}},{id:'year',get label(){return S().periods.year}},{id:'2year',get label(){return S().periods.twoYears}},{id:'all',get label(){return S().periods.all}}];



// ════════════ АНИМАЦИИ И ГЛОБАЛЬНАЯ СТАТИСТИКА ════════════

const style = document.createElement('style');
style.innerHTML = `
  @keyframes slideInTx {
    0% { max-height: 0; opacity: 0; transform: translateY(-20px) scale(0.98); margin-bottom: 0; padding-top: 0; padding-bottom: 0; border-bottom-width: 0; }
    100% { max-height: 2000px; opacity: 1; transform: translateY(0) scale(1); }
  }
  .anim-tx {
    animation: slideInTx 0.5s cubic-bezier(0.1, 0.8, 0.3, 1) forwards;
    overflow: hidden;
  }
  .anim-tx.anim-done {
    max-height: none;
    overflow: visible;
  }
  @keyframes helpTooltipIn {
    0% { opacity: 0; transform: translateY(6px) scale(0.96); }
    100% { opacity: 1; transform: translateY(0) scale(1); }
  }
  @keyframes helpTooltipOut {
    0% { opacity: 1; transform: translateY(0) scale(1); }
    100% { opacity: 0; transform: translateY(6px) scale(0.96); }
  }
  @keyframes helpIconPulse {
    0%,100% { box-shadow: 0 0 0 0 rgba(91,156,246,0.35); }
    50% { box-shadow: 0 0 0 6px rgba(91,156,246,0); }
  }
  .help-tooltip-icon {
    position: relative;
    display: inline-flex;
    align-items: center;
    justify-content: center;
    width: 26px; height: 26px;
    border-radius: 50%;
    cursor: pointer;
    transition: all 0.25s cubic-bezier(0.4,0,0.2,1);
    flex-shrink: 0;
  }
  .help-tooltip-icon:hover {
    transform: scale(1.15);
  }
  .help-tooltip-icon.pinned {
    animation: helpIconPulse 1.8s ease-in-out infinite;
  }
  .help-tooltip-popup {
    position: fixed;
    z-index: 9999;
    max-width: min(380px, 90vw);
    min-width: 220px;
    padding: 16px 18px;
    border-radius: 14px;
    font-size: 13px;
    line-height: 1.65;
    white-space: pre-line;
    pointer-events: auto;
    animation: helpTooltipIn 0.25s cubic-bezier(0.16,1,0.3,1) forwards;
    backdrop-filter: blur(16px);
    -webkit-backdrop-filter: blur(16px);
    box-shadow: 0 12px 40px rgba(0,0,0,0.25), 0 0 0 1px rgba(255,255,255,0.06);
  }
  .help-tooltip-popup.closing {
    animation: helpTooltipOut 0.18s ease-in forwards;
  }
  .help-tooltip-popup::before {
    content: '';
    position: absolute;
    top: -6px;
    left: 20px;
    width: 12px; height: 12px;
    border-radius: 2px;
    transform: rotate(45deg);
    pointer-events: none;
  }
  @media (max-width: 768px) {
    .help-tooltip-popup {
      max-width: calc(100vw - 32px);
      font-size: 12px;
      padding: 14px 16px;
    }
  }
`;
document.head.appendChild(style);

// ════════════ HELP TOOLTIP COMPONENT ════════════

export function HelpTooltip({ text, size = 16, style: customStyle }) {
  const t = useTheme();
  const [visible, setVisible] = useState(false);
  const [pinned, setPinned] = useState(false);
  const [closing, setClosing] = useState(false);
  const [pos, setPos] = useState({ top: 0, left: 0, arrowLeft: 20 });
  const iconRef = useRef(null);
  const popupRef = useRef(null);
  const hoverTimer = useRef(null);
  const leaveTimer = useRef(null);

  const isTouchDevice = typeof window !== 'undefined' && ('ontouchstart' in window || navigator.maxTouchPoints > 0);

  const calcPosition = () => {
    if (!iconRef.current) return;
    const rect = iconRef.current.getBoundingClientRect();
    const popW = Math.min(380, window.innerWidth * 0.9);
    const gap = 10;

    let top = rect.bottom + gap;
    let left = rect.left + rect.width / 2 - popW / 2;
    let arrowLeft = popW / 2 - 6;

    // Keep within viewport horizontally
    if (left < 16) {
      arrowLeft = Math.max(16, rect.left + rect.width / 2 - 16 - 6);
      left = 16;
    } else if (left + popW > window.innerWidth - 16) {
      const newLeft = window.innerWidth - popW - 16;
      arrowLeft = Math.max(16, Math.min(popW - 28, rect.left + rect.width / 2 - newLeft - 6));
      left = newLeft;
    }

    // If no room below, show above
    if (top + 120 > window.innerHeight) {
      top = rect.top - gap - 10; // will be adjusted by popup height
    }

    setPos({ top, left, arrowLeft });
  };

  const doShow = () => {
    calcPosition();
    setClosing(false);
    setVisible(true);
  };

  const doHide = (force) => {
    if (pinned && !force) return;
    setClosing(true);
    setTimeout(() => { setVisible(false); setClosing(false); setPinned(false); }, 180);
  };

  const handleMouseEnter = () => {
    if (isTouchDevice) return;
    clearTimeout(leaveTimer.current);
    hoverTimer.current = setTimeout(() => doShow(), 150);
  };

  const handleMouseLeave = () => {
    if (isTouchDevice) return;
    clearTimeout(hoverTimer.current);
    leaveTimer.current = setTimeout(() => { if (!pinned) doHide(); }, 200);
  };

  const handlePopupEnter = () => {
    if (isTouchDevice) return;
    clearTimeout(leaveTimer.current);
  };

  const handlePopupLeave = () => {
    if (isTouchDevice) return;
    leaveTimer.current = setTimeout(() => { if (!pinned) doHide(); }, 200);
  };

  const handleClick = (e) => {
    e.stopPropagation();
    e.preventDefault();
    if (pinned) {
      doHide(true);
    } else if (visible) {
      setPinned(true);
    } else {
      doShow();
      if (isTouchDevice) setPinned(true);
    }
  };

  // Close on outside click when pinned
  useEffect(() => {
    if (!pinned) return;
    const handler = (e) => {
      if (iconRef.current?.contains(e.target)) return;
      if (popupRef.current?.contains(e.target)) return;
      doHide(true);
    };
    document.addEventListener('mousedown', handler);
    document.addEventListener('touchstart', handler);
    return () => { document.removeEventListener('mousedown', handler); document.removeEventListener('touchstart', handler); };
  }, [pinned]);

  // Recalc on scroll/resize
  useEffect(() => {
    if (!visible) return;
    const update = () => calcPosition();
    window.addEventListener('scroll', update, true);
    window.addEventListener('resize', update);
    return () => { window.removeEventListener('scroll', update, true); window.removeEventListener('resize', update); };
  }, [visible]);

  const bgPopup = t.bg === '#252a3d' ? 'rgba(40,46,68,0.92)' : 'rgba(255,255,255,0.92)';
  const borderPopup = t.bg === '#252a3d' ? 'rgba(91,156,246,0.25)' : 'rgba(59,130,246,0.2)';
  const arrowBg = t.bg === '#252a3d' ? 'rgba(40,46,68,0.92)' : 'rgba(255,255,255,0.92)';

  return (
    <>
      <div
        ref={iconRef}
        className={`help-tooltip-icon${pinned ? ' pinned' : ''}`}
        onMouseEnter={handleMouseEnter}
        onMouseLeave={handleMouseLeave}
        onClick={handleClick}
        onTouchEnd={handleClick}
        style={{
          background: pinned ? `${t.accent}25` : `${t.accent}12`,
          color: t.accent,
          ...customStyle
        }}
      >
        <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
          <circle cx="12" cy="12" r="10"/>
          <path d="M9.09 9a3 3 0 0 1 5.83 1c0 2-3 3-3 3"/>
          <line x1="12" y1="17" x2="12.01" y2="17"/>
        </svg>
      </div>
      {visible && ReactDOM.createPortal(
        <div
          ref={popupRef}
          className={`help-tooltip-popup${closing ? ' closing' : ''}`}
          onMouseEnter={handlePopupEnter}
          onMouseLeave={handlePopupLeave}
          style={{
            top: pos.top,
            left: pos.left,
            background: bgPopup,
            border: `1.5px solid ${borderPopup}`,
            color: t.text,
          }}
        >
          <div style={{ position: 'absolute', top: -6, left: pos.arrowLeft, width: 12, height: 12, borderRadius: 2, transform: 'rotate(45deg)', background: arrowBg, borderLeft: `1.5px solid ${borderPopup}`, borderTop: `1.5px solid ${borderPopup}` }} />
          {pinned && (
            <div style={{ position: 'absolute', top: 8, right: 10, cursor: 'pointer', color: t.textMuted, opacity: 0.7, transition: 'opacity 0.2s' }} onClick={(e) => { e.stopPropagation(); doHide(true); }} onMouseEnter={e => e.currentTarget.style.opacity = 1} onMouseLeave={e => e.currentTarget.style.opacity = 0.7}>
              <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round"><line x1="18" y1="6" x2="6" y2="18"/><line x1="6" y1="6" x2="18" y2="18"/></svg>
            </div>
          )}
          <div style={{ fontWeight: 600, fontSize: 11, color: t.accent, marginBottom: 6, textTransform: 'uppercase', letterSpacing: 0.8, display: 'flex', alignItems: 'center', gap: 6 }}>
            <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round"><circle cx="12" cy="12" r="10"/><path d="M9.09 9a3 3 0 0 1 5.83 1c0 2-3 3-3 3"/><line x1="12" y1="17" x2="12.01" y2="17"/></svg>
            Справка
          </div>
          {text}
        </div>,
        document.body
      )}
    </>
  );
}

export function AnimNum({ value }) {
  const [val, setVal] = useState(value);
  useEffect(() => {
    let start = null;
    const initVal = val;
    if (initVal === value) return;
    const step = (ts) => {
      if (!start) start = ts;
      const prog = Math.min((ts - start) / 1200, 1);
      const ease = 1 - Math.pow(1 - prog, 3);
      setVal(initVal + (value - initVal) * ease);
      if (prog < 1) window.requestAnimationFrame(step);
    };
    window.requestAnimationFrame(step);
  }, [value]);
  return <>{fmt(val)}</>;
}

// НОВОЕ: Полоска "Бегущей строки" (Тикер) с перетаскиванием мышкой (Drag-to-scroll)
export function GlobalStats({ data, aAccs, aFunds, isCompact }) {
  const t = useTheme();
  // Скрытые счета (isHidden) полностью исключаем из шапки: их не должно быть в строке
  // и их балансы не должны входить в "Итого на счетах"
  const visibleAccs = (aAccs || []).filter(a => !a.isHidden);
  const tAcc = visibleAccs.reduce((s, a) => s + (data.accountBalances[a.id] || 0), 0);

  // --- ЛОГИКА ПЕРЕТАСКИВАНИЯ (DRAG-TO-SCROLL) ---
  const scrollRef = useRef(null);
  const [isDragging, setIsDragging] = useState(false);
  const [startX, setStartX] = useState(0);
  const [scrollLeft, setScrollLeft] = useState(0);

  const handleMouseDown = (e) => {
    setIsDragging(true);
    setStartX(e.pageX - scrollRef.current.offsetLeft);
    setScrollLeft(scrollRef.current.scrollLeft);
  };
  const handleMouseLeave = () => setIsDragging(false);
  const handleMouseUp = () => setIsDragging(false);
  const handleMouseMove = (e) => {
    if (!isDragging) return;
    e.preventDefault(); // Останавливаем выделение текста
    const x = e.pageX - scrollRef.current.offsetLeft;
    const walk = (x - startX) * 1.5; // 1.5 - множитель скорости прокрутки
    scrollRef.current.scrollLeft = scrollLeft - walk;
  };

  return (
    <div 
      ref={scrollRef}
      className="hide-scroll" 
      onMouseDown={handleMouseDown}
      onMouseLeave={handleMouseLeave}
      onMouseUp={handleMouseUp}
      onMouseMove={handleMouseMove}
      style={{ 
        display: 'flex', 
        flexWrap: 'nowrap', 
        alignItems: 'center', 
        width: '100%', 
        padding: '8px 20px', // Уменьшили отступы
        background: t.bgInput, 
        borderBottom: `1px solid ${t.border}`, 
        overflowX: 'auto', 
        whiteSpace: 'nowrap',
        cursor: isDragging ? 'grabbing' : 'grab', // Иконка "хватающей руки"
        userSelect: 'none', // Чтобы текст не выделялся синим при перетаскивании
        WebkitUserSelect: 'none'
      }}
    >
      
      <div style={{ display: 'flex', alignItems: 'center', gap: 8, paddingRight: 16, borderRight: `1.5px solid ${t.border}`, flexShrink: 0, marginRight: 16 }}>
        <span style={{ color: t.textMuted, fontWeight: 800, fontSize: 10, textTransform: 'uppercase', letterSpacing: 1 }}>{S().globalStats.totalOnAccounts}</span>
        <span className="mono" style={{ fontWeight: 800, fontSize: 14, color: t.text }}><AnimNum value={tAcc}/></span>
      </div>
      
      <div style={{ display: 'flex', flexWrap: 'nowrap', alignItems: 'center', gap: 10, flexShrink: 0 }}>
        {visibleAccs.map(a => {
          const bal = data.accountBalances[a.id] || 0;
          return (
            <div key={a.id} style={{ display: 'flex', alignItems: 'center', gap: 6, padding: '3px 8px', borderRadius: 8, background: `${a.color}15`, border: `1px solid ${a.color}40`, flexShrink: 0 }}>
              <span style={{ color: a.color, fontWeight: 800, fontSize: 11, letterSpacing: 0.5 }}>{a.name.slice(0, 3).toUpperCase()}</span>
              <span className="mono" style={{ fontWeight: 700, fontSize: 12, color: bal > 0 ? a.color : t.textMuted }}><AnimNum value={bal}/></span>
            </div>
          );
        })}

        {visibleAccs.length > 0 && aFunds.length > 0 && <div style={{ width: 2, height: 14, background: t.border, margin: '0 2px', flexShrink: 0, borderRadius: 2 }} />}
        
        {aFunds.map(f => {
          const bal = data.fundBalances[f.id] || 0;
          return (
            <div key={f.id} style={{ display: 'flex', alignItems: 'center', gap: 6, padding: '3px 8px', borderRadius: 8, background: `${f.color}15`, border: `1px solid ${f.color}40`, flexShrink: 0 }}>
              <span style={{ color: f.color, fontWeight: 800, fontSize: 11, letterSpacing: 0.5 }}>{f.name.slice(0, 3).toUpperCase()}</span>
              <span className="mono" style={{ fontWeight: 700, fontSize: 12, color: bal > 0 ? f.color : t.textMuted }}><AnimNum value={bal}/></span>
            </div>
          );
        })}
      </div>
    </div>
  );
}

// ════════════ SEARCHABLE SELECT ════════════
export function SearchableSelect({ value, onChange, options, placeholder = "Выберите", disabled = false, style = {} }) {
  const t = useTheme();
  const [open, setOpen] = useState(false);
  const [search, setSearch] = useState('');
  const ref = useRef(null);

  useEffect(() => {
    const fn = (e) => { if (ref.current && !ref.current.contains(e.target)) setOpen(false); };
    document.addEventListener('mousedown', fn);
    return () => document.removeEventListener('mousedown', fn);
  }, []);

  const selectedOpt = options.find(o => String(o.value) === String(value));
  
  const filtered = options.filter(o => {
    if (!search) return true;
    const q = search.toLowerCase();
    const l1 = (o.label || '').toLowerCase();
    const l2 = (o.subLabel || '').toLowerCase();
    return l1.includes(q) || l2.includes(q);
  });

  return (
    <div ref={ref} style={{ position: 'relative', width: '100%', ...style }}>
      <div 
        onClick={() => !disabled && setOpen(!open)}
        style={{
          display: 'flex', alignItems: 'center', justifyContent: 'space-between',
          padding: '10px 14px', borderRadius: 8, background: disabled ? t.bgCard : t.bgInput,
          border: `1px solid ${open ? t.accent : t.border}`, color: disabled ? t.textMuted : t.text,
          cursor: disabled ? 'not-allowed' : 'pointer', fontSize: 14, minHeight: 40
        }}
      >
        <div style={{ whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
          {selectedOpt ? (
             <span>{selectedOpt.label} {selectedOpt.subLabel && <span style={{fontSize: 12, color: t.textMuted}}>- {selectedOpt.subLabel}</span>}</span>
          ) : (
            <span style={{ color: t.placeholder }}>{placeholder}</span>
          )}
        </div>
        <ChevronDown size={16} color={t.textMuted} />
      </div>

      {open && (
        <div style={{
          position: 'absolute', top: '100%', left: 0, right: 0, marginTop: 4,
          background: t.bgCard, border: `1px solid ${t.border}`, borderRadius: 12,
          boxShadow: t.shadow, zIndex: 99999, maxHeight: 300, display: 'flex', flexDirection: 'column',
          overflow: 'hidden'
        }}>
          <div style={{ padding: '10px 12px', display: 'flex', alignItems: 'center', gap: 8, borderBottom: `1px solid ${t.border}`, background: t.bgInput }}>
            <Search size={16} color={t.textMuted} />
            <input 
              autoFocus
              value={search} onChange={e => setSearch(e.target.value)}
              placeholder="Поиск по названию или БИН/ИИН..."
              style={{ padding: 0, border: 'none', background: 'transparent', outline: 'none', fontSize: 13, width: '100%' }}
            />
          </div>
          <div className="hide-scroll" style={{ overflowY: 'auto', padding: 6, flex: 1 }}>
            <div 
              onClick={() => { onChange(''); setOpen(false); setSearch(''); }} 
              style={{ padding: '10px 12px', cursor: 'pointer', fontSize: 13, borderRadius: 8, 
                       background: !value ? `${t.accent}15` : 'transparent',
                       color: !value ? t.accent : t.text, fontWeight: !value ? 700 : 500 }}
              onMouseEnter={e => { if (value) e.currentTarget.style.background = t.bgInput; }}
              onMouseLeave={e => { if (value) e.currentTarget.style.background = 'transparent'; }}
            >
              {placeholder}
            </div>
            {filtered.length === 0 ? (
              <div style={{ padding: '16px 10px', fontSize: 13, color: t.textMuted, textAlign: 'center' }}>Совпадений не найдено</div>
            ) : (
              filtered.map(o => (
                <div 
                  key={o.value} 
                  onClick={() => { onChange(o.value); setOpen(false); setSearch(''); }}
                  style={{ 
                    padding: '8px 12px', cursor: 'pointer', fontSize: 13, borderRadius: 8,
                    background: String(value) === String(o.value) ? `${t.accent}15` : 'transparent',
                    color: String(value) === String(o.value) ? t.accent : t.text,
                    display: 'flex', flexDirection: 'column', gap: 2, marginTop: 2
                  }}
                  onMouseEnter={e => { if (String(value) !== String(o.value)) e.currentTarget.style.background = t.bgInput; }}
                  onMouseLeave={e => { if (String(value) !== String(o.value)) e.currentTarget.style.background = 'transparent'; }}
                >
                  <span style={{fontWeight: String(value) === String(o.value) ? 700 : 500}}>{o.label}</span>
                  {o.subLabel && <span style={{fontSize: 11, color: t.textMuted}}>{o.subLabel}</span>}
                </div>
              ))
            )}
          </div>
        </div>
      )}
    </div>
  );
}